/**
 * The small, pure pieces behind an announcement's attachments — TROOP-255.
 *
 * Self-contained on purpose: `npm test` runs these under Node's own runner, which resolves imports
 * literally, so this file reaches for nothing else in `src/`.
 *
 * **The server is the judge of every one of these limits.** The numbers are repeated here so the
 * form can say them before a round trip, not so the form can decide anything — a client that
 * disagrees with the server is refused by it.
 */

/** Documents and links together, per announcement. The form states it and stops offering adds. */
export const ATTACHMENT_LIMIT = 5;

/** A label's length after trimming, on both kinds. */
export const ATTACHMENT_LABEL_MAX = 120;

/** A link's address, as typed. */
export const ATTACHMENT_URL_MAX = 2048;

/**
 * What a file weighs, in the words a parent reads. Kilobytes rounded and never below one, as the
 * requirement's blank form has always printed it; megabytes to one decimal once it gets there.
 */
export function formatByteSize(bytes: number): string {
  const kb = bytes / 1024;

  if (kb < 1024) {
    return `${Math.max(1, Math.round(kb))} KB`;
  }

  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/**
 * The word for a document's type, as the caption beside it shows it. The server accepts a PDF or
 * a JPEG, PNG or WebP picture; anything else it might one day send reads as a plain "File" rather
 * than as a raw media type.
 */
export function documentKindLabel(contentType: string): string {
  switch (contentType.toLowerCase()) {
    case 'application/pdf':
      return 'PDF';
    case 'image/jpeg':
      return 'JPEG';
    case 'image/png':
      return 'PNG';
    case 'image/webp':
      return 'WebP';
    default:
      return contentType.toLowerCase().startsWith('image/') ? 'Picture' : 'File';
  }
}

/**
 * A label to start from when somebody picks a file: its name without the extension, cut to the
 * label limit. Editable on the form; this is only where it begins. A name that is all extension
 * (".pdf") keeps what it has rather than becoming empty.
 */
export function labelFromFileName(fileName: string): string {
  const name = fileName.trim();
  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot).trim() : name;

  return stem.slice(0, ATTACHMENT_LABEL_MAX);
}

/**
 * Why the form would refuse a typed address before sending it, or null when it has nothing to say.
 *
 * Mirrors what the server refuses — not absolute, not https, a user name or password before the
 * host, too long — so the message arrives while the box still has focus. **It is a courtesy, not
 * the rule**: the server checks again, and a null here is not an acceptance.
 */
export function linkAddressProblem(address: string): string | null {
  const typed = address.trim();

  if (typed.length > ATTACHMENT_URL_MAX) {
    return `That address is too long — ${ATTACHMENT_URL_MAX} characters at most.`;
  }

  let url: URL;
  try {
    url = new URL(typed);
  } catch {
    return 'Paste the whole address, starting with https://';
  }

  if (url.protocol !== 'https:') {
    return 'Only https:// addresses can be attached.';
  }

  if (url.username || url.password) {
    return 'That address has a name or password in it. Paste the plain address instead.';
  }

  return null;
}

/** How long before a signature lapses the screen asks for fresh ones. */
export const RESIGN_MARGIN_MS = 30_000;

/**
 * The soonest a re-list is scheduled. A minute rather than a few seconds because of the case it
 * really exists for: a phone whose clock runs fast sees every signature as already lapsed on every
 * re-list, and at a floor of five seconds that board re-signed up to 125 addresses every five
 * seconds for as long as it stayed open. A minute bounds that, and a correctly-set clock never
 * reaches it — the margin above leaves four and a half minutes.
 */
export const RESIGN_FLOOR_MS = 60_000;

/**
 * How long to wait before re-listing so that no document link on screen has lapsed when it is
 * tapped, or null when there is nothing signed to keep fresh. The URLs live five minutes; a
 * noticeboard is a page people leave open for an afternoon (TROOP-255).
 *
 * Takes the `expiresAt` instants as strings, exactly as the API sends them; one that does not
 * parse is ignored rather than scheduling a re-list for "now" forever.
 */
export function msUntilResign(expiries: readonly string[], now: number): number | null {
  const instants = expiries.map((e) => Date.parse(e)).filter((t) => !Number.isNaN(t));

  if (instants.length === 0) {
    return null;
  }

  return Math.max(RESIGN_FLOOR_MS, Math.min(...instants) - now - RESIGN_MARGIN_MS);
}

/**
 * The list with one item moved a place up (-1) or down (+1). Moving past either end returns the
 * list unchanged rather than throwing — the form omits those controls anyway.
 */
export function moveItem<T>(items: readonly T[], index: number, delta: -1 | 1): T[] {
  const target = index + delta;

  if (index < 0 || index >= items.length || target < 0 || target >= items.length) {
    return [...items];
  }

  const moved = [...items];
  [moved[index], moved[target]] = [moved[target], moved[index]];
  return moved;
}
